import React from 'react';
import { Button, Card, Input, Segmented, Tag, Typography } from 'antd';
import { ClearOutlined, SearchOutlined, ThunderboltOutlined } from '@ant-design/icons';
import { LeadOpsMode } from '../hooks/useLeadOpsMode';

type SearchBarProps = {
  domainInput: string;
  onDomainChange: (value: string) => void;
  onHistorySelect?: (domain: string) => void;
  historyItems?: Array<{ domain: string }>;
  onCheckSw: () => void;
  onCheckMonday: () => void;
  onCheckBoth: () => void;
  isLoadingSw: boolean;
  isLoadingMonday: boolean;
  isLoadingBoth: boolean;
  lastAction: 'sw' | 'monday' | 'both' | null;
  mode: LeadOpsMode;
  onModeChange: (mode: LeadOpsMode) => void;
  showClear?: boolean;
  onClear?: () => void;
};

export const SearchBar: React.FC<SearchBarProps> = ({
  domainInput,
  onDomainChange,
  onHistorySelect,
  historyItems = [],
  onCheckSw,
  onCheckMonday,
  onCheckBoth,
  isLoadingSw,
  isLoadingMonday,
  isLoadingBoth,
  lastAction,
  mode,
  onModeChange,
  showClear = false,
  onClear,
}) => (
  <Card id="leadops-search-card" className="leadops-search-card">
    <div className="leadops-mode-row">
      <Segmented<LeadOpsMode>
        className="leadops-mode-switch"
        value={mode}
        onChange={onModeChange}
        options={[
          { label: 'Publisher', value: 'publisher' },
          { label: 'Advertiser', value: 'advertiser' },
          { label: 'UNSOLD', value: 'unsold' },
        ]}
      />
      {showClear && onClear && (
        <Button className="leadops-clear-results" icon={<ClearOutlined />} onClick={onClear}>
          Clear results
        </Button>
      )}
    </div>
    <div className="leadops-search-row">
      <div id="leadops-domain-input" className="leadops-domain-input">
        <Input
          size="middle"
          placeholder="example.com"
          value={domainInput}
          onChange={(e) => onDomainChange(e.target.value)}
          onPressEnter={onCheckBoth}
          prefix={<SearchOutlined />}
        />
      </div>
      <div className="leadops-check-actions">
        {(() => {
          const anyLoading = isLoadingSw || isLoadingMonday || isLoadingBoth;
          return (
            <>
              <Button
                className="leadops-check-sw"
                icon={<SearchOutlined />}
                onClick={onCheckSw}
                loading={isLoadingSw && lastAction === 'sw'}
                disabled={anyLoading && !(isLoadingSw && lastAction === 'sw')}
              >
                <span className="desktop-label">Similarweb</span>
                <span className="mobile-label">SW</span>
              </Button>
              <Button
                className="leadops-check-monday"
                icon={<span className="monday-mark" aria-hidden="true"><span /><span /><span /></span>}
                onClick={onCheckMonday}
                loading={isLoadingMonday && lastAction === 'monday'}
                disabled={anyLoading && !(isLoadingMonday && lastAction === 'monday')}
              >
                Monday
              </Button>
              <Button
                id="leadops-check-both"
                className="leadops-check-both"
                type="primary"
                icon={<ThunderboltOutlined />}
                onClick={onCheckBoth}
                loading={isLoadingBoth && lastAction === 'both'}
                disabled={anyLoading && !(isLoadingBoth && lastAction === 'both')}
              >
                Both
              </Button>
            </>
          );
        })()}
      </div>
    </div>
    {historyItems.length > 0 && onHistorySelect && (
      <div className="leadops-search-meta">
        <div className="leadops-search-context">
          <div className="leadops-recent-strip">
            <Typography.Text type="secondary">
              <span className="desktop-label">Recent searches</span>
              <span className="mobile-label">Recent</span>
            </Typography.Text>
            <div className="leadops-recent-items">
              {historyItems.slice(0, 8).map((item) => (
                <Tag key={item.domain} onClick={() => onHistorySelect(item.domain)}>{item.domain}</Tag>
              ))}
            </div>
          </div>
        </div>
      </div>
    )}
  </Card>
);
